import { useEffect } from 'react'

/**
 * Show one page in a fixed theme, without touching the stored preference (#1054).
 *
 * `ThemeProvider` puts the OS theme class on <html>/<body> on every route. A
 * page outside the desktop — the public survey link — then inherits it: the
 * contrast-dark theme forces light text on headings, labels and paragraphs
 * with `!important`, while its dark window background only exists inside app
 * windows, so the page showed white text on white. The classes and
 * `data-theme` are swapped for the page's own theme on mount and put back on
 * unmount; `localStorage` is not written, so the desktop keeps the person's
 * choice.
 */
export function usePageTheme(theme: string) {
  useEffect(() => {
    const targets = [document.documentElement, document.body]
    const saved = targets.map((el) => ({
      el,
      className: el.className,
      dataTheme: el.getAttribute('data-theme'),
    }))

    for (const el of targets) {
      const kept = Array.from(el.classList).filter((c) => !isThemeClass(c))
      el.className = [...kept, theme].join(' ')
      el.setAttribute('data-theme', theme)
    }

    return () => {
      for (const { el, className, dataTheme } of saved) {
        el.className = className
        if (dataTheme === null) el.removeAttribute('data-theme')
        else el.setAttribute('data-theme', dataTheme)
      }
    }
  }, [theme])
}

const THEME_CLASSES = new Set(['light', 'dark', 'cdb-light', 'cdb-dark'])

function isThemeClass(name: string): boolean {
  return THEME_CLASSES.has(name) || name.startsWith('theme-')
}
